import React from "react";
import { Link } from "react-router-dom";
import { Utensils, ShoppingBag, Sparkles, Truck, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { RESTAURANT, IMG } from "@/data/restaurant";

const SERVICES = [
  {
    icon: Utensils,
    title: "Dine In",
    blurb:
      "An intimate, candle-lit room of 38 covers. Open kitchen, live tandoor, and a curated bar. Reserve a table for an unhurried, contemporary Indian dining experience.",
    cta: { label: "Book a Table", href: RESTAURANT.bookUrl, external: true, testid: "service-dinein-cta" },
    img: IMG.interior1,
  },
  {
    icon: ShoppingBag,
    title: "Takeaway & Delivery",
    blurb:
      "Order our full main menu for collection or doorstep delivery across St Albans. Every dish is hand-finished, sealed warm, and ready in 25 minutes.",
    cta: { label: "Order Online", href: RESTAURANT.orderUrl, external: true, testid: "service-takeaway-cta" },
    img: "https://images.pexels.com/photos/4393426/pexels-photo-4393426.jpeg?auto=compress&cs=tinysrgb&w=1000",
  },
  {
    icon: Sparkles,
    title: "Private Events",
    blurb:
      "Exclusive hire for milestones, anniversaries, corporate dinners and intimate weddings — bespoke menus designed with our head chef, paired with cocktails and a dedicated host.",
    cta: { label: "Enquire", to: "/contact", testid: "service-events-cta" },
    img: "https://images.pexels.com/photos/941861/pexels-photo-941861.jpeg?auto=compress&cs=tinysrgb&w=1000",
  },
  {
    icon: Truck,
    title: "Catering",
    blurb:
      "Off-site catering for celebrations, offices and weddings across Hertfordshire & North London. From canapé arrivals to full thali experiences — built around your guest count.",
    cta: { label: "Get a Quote", to: "/contact", testid: "service-catering-cta" },
    img: IMG.food1,
  },
];

export default function OurServices() {
  return (
    <section data-testid="services-section" className="relative py-24 md:py-32 bg-[var(--infuse-cream)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="gold-divider font-eyebrow text-[var(--infuse-gold)]">Our Services</span>
              <h2 className="font-display text-5xl md:text-6xl mt-5 text-[var(--infuse-emerald)] leading-[1] max-w-3xl">
                Four ways to taste <em className="not-italic text-[var(--infuse-pomegranate)]">Infuse.</em>
              </h2>
            </div>
            <p className="text-[var(--infuse-ink)]/65 max-w-sm leading-relaxed">
              Whether you're dining in, ordering home, hosting a celebration, or feeding a crowd — we bring the same craft to every plate.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 gap-6">
          {SERVICES.map(({ icon: Icon, title, blurb, cta, img }, i) => (
            <Reveal key={title} delay={i * 70}>
              <article
                data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group relative h-full bg-white border border-[var(--infuse-gold)]/25 hover:border-[var(--infuse-gold)]/70 transition-all duration-500 overflow-hidden"
              >
                <div className="grid md:grid-cols-[180px_1fr] h-full">
                  <div className="relative h-44 md:h-full overflow-hidden">
                    <img
                      src={img}
                      alt={title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/40 to-transparent" />
                    <div className="absolute top-4 left-4 w-11 h-11 flex items-center justify-center bg-[var(--infuse-cream)] border border-[var(--infuse-gold)]/40">
                      <Icon size={20} strokeWidth={1.5} className="text-[var(--infuse-pomegranate)]" />
                    </div>
                  </div>
                  <div className="p-7 md:p-9 flex flex-col">
                    <h3 className="font-display text-3xl text-[var(--infuse-emerald)] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-3 text-[var(--infuse-ink)]/70 leading-relaxed flex-1 text-[0.97rem]">
                      {blurb}
                    </p>
                    <div className="mt-6">
                      {cta.external ? (
                        <a
                          href={cta.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={cta.testid}
                          className="inline-flex items-center gap-2 font-eyebrow text-[0.7rem] text-[var(--infuse-pomegranate)] hover:gap-3 transition-all"
                        >
                          {cta.label} <ChevronRight size={14} />
                        </a>
                      ) : (
                        <Link
                          to={cta.to}
                          data-testid={cta.testid}
                          className="inline-flex items-center gap-2 font-eyebrow text-[0.7rem] text-[var(--infuse-pomegranate)] hover:gap-3 transition-all"
                        >
                          {cta.label} <ChevronRight size={14} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
